import { useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Loader2, ChevronRight, ChevronDown, Brain, Zap } from "lucide-react";
import api from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

/**
 * Single variance-alert row with expandable AI analysis.
 *
 * For variances ≥ 25% (in absolute value), clicking the row auto-fetches
 * the LLM-generated causes + corrective action via
 *   POST /api/restaurants/{rid}/variance-alerts/causes/{ingredient_id}
 * The response is cached daily per (restaurant, ingredient, date).
 */
export function VarianceAlertRow({ alert, restaurantId }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [causes, setCauses] = useState(null);
  const [error, setError] = useState(null);
  const severe = Math.abs(alert.delta_pct) >= 25;
  const testid = `variance-alert-row-${alert.ingredient_id}`;

  const fetchCauses = async (force = false) => {
    if (!restaurantId || !alert.ingredient_id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getVarianceCauses(restaurantId, alert.ingredient_id, force);
      if (data.error) {
        setError(data.error === "llm_failed" ? "La IA no está disponible ahora, inténtalo de nuevo." : "No se pudo analizar.");
      } else {
        setCauses(data);
      }
    } catch (e) {
      setError(e?.message || "Error desconocido");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    if (!expanded && severe && !causes) {
      fetchCauses(false);
    }
    setExpanded(!expanded);
  };

  const probabilityColor = (p) => {
    if (p === "alta") return "bg-red-100 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900";
    if (p === "media") return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900";
    return "bg-muted text-foreground border-border dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
  };

  return (
    <div className="border-t border-border/40 first:border-t-0" data-testid={testid}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between text-xs py-1.5 hover:bg-muted/40 rounded px-1 transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <span className="flex items-center gap-1.5">
          {severe ? (
            expanded ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-3 h-3 text-muted-foreground/40" />
          )}
          <span className="font-medium">{alert.name}</span>
          {severe && !causes && !loading && (
            <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-900">
              <Brain className="w-2.5 h-2.5 mr-0.5" /> IA
            </Badge>
          )}
        </span>
        <span className="flex items-center gap-3 font-mono">
          <span className="text-muted-foreground">
            {alert.actual}{alert.unit} / {alert.expected}{alert.unit}
          </span>
          <span className="text-red-700 font-semibold">{alert.delta_pct}%</span>
          <span className="font-semibold">{formatCurrency(alert.loss_value)}</span>
        </span>
      </button>

      {expanded && severe && (
        <div className="px-3 pb-3 pt-2 space-y-2" data-testid={`${testid}-analysis`}>
          {loading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Analizando con IA…
            </div>
          )}
          {error && (
            <div className="text-xs text-red-700 flex items-center justify-between">
              <span>{error}</span>
              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => fetchCauses(true)}>
                Reintentar
              </Button>
            </div>
          )}
          {causes && !loading && (
            <>
              <div className="space-y-1.5">
                {causes.causes?.map((c, idx) => (
                  <div key={idx} className="text-xs flex gap-2 items-start" data-testid={`${testid}-cause-${idx}`}>
                    <Badge variant="outline" className={`text-[9px] h-4 px-1.5 flex-shrink-0 ${probabilityColor(c.probability)}`}>
                      {c.probability}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">{c.title}</p>
                      <p className="text-muted-foreground mt-0.5 leading-relaxed">{c.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              {causes.action && (
                <div className="mt-2 p-2.5 rounded-md bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900" data-testid={`${testid}-action`}>
                  <p className="text-[10px] uppercase tracking-wide font-bold text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                    <Zap className="w-3 h-3" /> Acción recomendada
                  </p>
                  <p className="text-xs mt-1 text-emerald-900 dark:text-emerald-200 leading-relaxed">{causes.action}</p>
                </div>
              )}
              <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1">
                <span>{causes._provider} {causes.cached ? "· en caché" : "· nuevo"}</span>
                <Button
                  size="sm" variant="ghost" className="h-6 text-[10px]"
                  onClick={() => fetchCauses(true)}
                  disabled={loading}
                  data-testid={`${testid}-regenerate`}
                >
                  Regenerar
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
